/*
 * GCC — GROUP CHAT WITH CATCHY (Jay, 2026-10-27; the server half is gcc.js).
 *
 * Three columns: the rooms, the chat, and Catchy's side window — "like a live stream chat": Catchy follows the
 * conversation and posts there on its own, so nobody has to tag it, and each person turns the window on or off (kept
 * per room on the server). @Catchy in a message still asks it directly; that answer appears in the chat itself.
 *
 * New messages are polled every few seconds while the tab is visible (POLL_MS), the room list less often. Staging only
 * for now — behind REACT_APP_GCC; the server half is GCC=1.
 *
 * 29/10 (Jay's feedback, Max's "tag người khác"): @name picks a member from a list (they get their own bell line); an emoji
 * button beside the box and reactions at the bottom right of a message; images and files — attached, dropped, or a
 * screenshot pasted with Ctrl+V; and each person's own translation switch, which counts from the moment it is turned on
 * (the messages above are translated only when asked).
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Send, UserPlus, Pencil, LogOut, Trash2, X, MessagesSquare, ChevronsLeft, ChevronsRight, Smile, Paperclip, FileText, Languages, SmilePlus, Download, ChevronLeft, ChevronRight, UserMinus, Reply, ChevronUp, MonitorUp, CalendarClock, CalendarDays, Camera, PictureInPicture2, Bold, Italic, Underline, Strikethrough, List, ListOrdered, Code, Baseline } from 'lucide-react';
import { desktopOn, desktopSupported, setDesktop } from './gccAlerts';
import Header from './Header';
import { Mascot } from './AskBubble';
import { plainlyIn } from './aiTranslate';
import { enterSends, isSmall } from './touchInput';
import { popOutRoom } from './GccPopups';
import UserAvatar from './UserAvatar';
import useAuth from './useAuth';
import { BACKEND_URL } from './backendConfig';
import './SPHub.css';
import './ProjectApproval.css';
import './EusuStock.css';
import ModuleHelp from './ModuleHelp';
import './Gcc.css';
import { dateLocale } from './l10nField';

const API = `${BACKEND_URL}/api/gcc`;
const POLL_MS = 2500;
const ROOMS_MS = 10000;
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/* a stored UTC time in Vietnam: "15:42" today, "27/Oct 15:42" before */
const when = (v) => {
  if (!v) return '';
  const d = new Date(String(v).endsWith('Z') || String(v).includes('+') ? v : `${v}Z`);
  if (Number.isNaN(d.getTime())) return '';
  const vn = new Date(d.getTime() + 7 * 3600000);
  const now = new Date(Date.now() + 7 * 3600000);
  const hm = vn.toISOString().slice(11, 16);
  return vn.toISOString().slice(0, 10) === now.toISOString().slice(0, 10) ? hm : `${String(vn.getUTCDate()).padStart(2, '0')}/${MON[vn.getUTCMonth()]} ${hm}`;
};

/* the reader's portal language, as aiTranslate.js reads it */
const readLang = () => { const l = String(i18n.language || '').slice(0, 2); return ['ko', 'vi', 'zh', 'fr', 'ja'].includes(l) ? l : 'en'; };
const call = async (method, url, body) => {
  const r = await fetch(`${API}${url}`, {
    method, credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
};

const escRe = (x) => String(x).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const fold = (x) => String(x || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();
const EMOJIS = ['😀', '😄', '😂', '🤣', '😊', '😍', '😘', '😎', '🤔', '😅', '😢', '😭', '😡', '😱', '😴', '🙄', '🥳', '🤗', '🙏', '👍', '👎', '👏', '🙌', '💪',
  '👌', '✌️', '🤝', '👀', '❤️', '🔥', '✨', '🎉', '✅', '❌', '⚠️', '❓', '💯', '📌', '📎', '📦', '🚚', '🏕️', '🪑', '☕', '🍜', '⏰', '📅', '💡'];
const QUICK = ['👍', '❤️', '😂', '😮', '😢', '🙏', '✅', '🎉'];
const fileSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

/* GCC'S OWN SMALL MARKUP (Max 02/11: "formatting cho tin nhắn — đậm, nghiêng, number list, bullet list"): **bold**, _italic_,
   __underline__, ~~strike~~, `code`; lines starting "- " / "• " are a bulleted list, "1. " a numbered one. The message stays
   plain text in the database — what Catchy, the translator and the bell read — and the composer's toolbar writes the same
   marks. _italic_ only between non-letters, so file_name_v2 stays as it is. */
const MARKS = [[/^\*\*([^*\n]+)\*\*$/, 'strong'], [/^__([^_\n]+)__$/, 'u'], [/^~~([^~\n]+)~~$/, 's'], [/^`([^`\n]+)`$/, 'code'], [/^_([^_\n]+)_$/, 'em']];
/* NO LOOKBEHIND: Safari before iOS 16.4 cannot even build a regex with (?<! …) — the room threw "invalid group specifier name" and
   showed no messages on Max's iPhone (04/11). "Not after a letter" for _italic_ is checked in `inline` on the text before it. */
const MARK_RE = '\\*\\*[^*\\n]+\\*\\*|__[^_\\n]+__|~~[^~\\n]+~~|`[^`\\n]+`|_[^_\\n]+_(?![A-Za-z0-9_\\u00C0-\\u1EF9])';
const WORD_END = /[A-Za-z0-9_\u00C0-\u1EF9]$/;
const LIST_B = /^\s*[-•*]\s+(.*)$/;
const LIST_N = /^\s*(\d{1,3})[.)]\s+(.*)$/;
/* the marks taken out — for one-line previews (room list, quoted reply) */
const plainText = (x) => String(x || '').replace(/\*\*|__|~~|`/g, '').replace(/(^|\s)_([^_\n]+)_(?=\s|$)/g, '$1$2');
/* a message's text: the marks above, @Catchy and @tagged people marked (the reader's own name stronger), line breaks kept */
const Text = ({ text, tags = [], meId }) => {
  const names = tags.flatMap((p) => [p.name, String(p.name || '').split(' ')[0]].filter(Boolean).map((n) => ({ n, me: Number(p.id) === Number(meId) })))
    .sort((a, b) => b.n.length - a.n.length);
  const tagRe = names.length ? `|@(?:${names.map((x) => escRe(x.n)).join('|')})` : '';
  const split = new RegExp(`(${MARK_RE}|@catchy\\b${tagRe})`, 'gi');
  const inline = (str, key) => str.split(split).map((p, k, all) => {
    if (!p) return null;
    const kk = `${key}.${k}`;
    // _italic_ only when it does not follow a letter (file_name_v2 stays as typed) — the lookbehind the regex may not have
    const found = MARKS.find(([re]) => re.test(p));
    const mk = found && !(p[0] === '_' && p[1] !== '_' && WORD_END.test(all[k - 1] || '')) ? found : null;
    if (mk) {
      const inner = p.match(mk[0])[1];
      return React.createElement(mk[1], { key: kk, className: mk[1] === 'code' ? 'gc-code' : undefined }, mk[1] === 'code' ? inner : inline(inner, kk));
    }
    if (/^@catchy$/i.test(p)) return <span key={kk} className="gc-at">{p}</span>;
    const hit = p.startsWith('@') && names.find((x) => x.n.toLowerCase() === p.slice(1).toLowerCase());
    if (hit) return <span key={kk} className={`gc-at gc-tag${hit.me ? ' gc-tag-me' : ''}`}>{p}</span>;
    return p;
  });
  // consecutive list lines become one list; everything else stays a line
  const blocks = [];
  String(text || '').split('\n').forEach((line) => {
    const b = line.match(LIST_B); const n = !b && line.match(LIST_N);
    if (b || n) {
      const kind = b ? 'ul' : 'ol'; const last = blocks[blocks.length - 1];
      if (last && last.kind === kind) last.items.push(b ? b[1] : n[2]);
      else blocks.push({ kind, start: n ? Number(n[1]) : 1, items: [b ? b[1] : n[2]] });
    } else blocks.push({ kind: 'line', line });
  });
  return blocks.map((x, i) => {
    if (x.kind === 'line') {
      const next = blocks[i + 1];
      // eslint-disable-next-line react/no-array-index-key
      return <React.Fragment key={i}>{inline(x.line, i)}{next && next.kind === 'line' && <br />}</React.Fragment>;
    }
    const Tag = x.kind;
    // eslint-disable-next-line react/no-array-index-key
    return <Tag key={i} className="gc-list" start={x.kind === 'ol' && x.start !== 1 ? x.start : undefined}>{x.items.map((it, k) => <li key={k}>{inline(it, `${i}.${k}`)}</li>)}</Tag>;
  });
};

/* A picture opened from a message — in a window ON the page, never a new browser tab (Jay, 29/10: "Pop up window, not new
   Chrome tab"). Dark backdrop, the image fitted to the screen; close with ✕, Esc or a click outside; ← / → walk the
   message's other pictures; Download keeps the file. Drawn into <body> above every GCC layer (the side window, the
   room's own popups at 3000/3100) so nothing sits on top of its buttons. */
function ImageViewer({ list, start, onClose }) {
  const { t } = useTranslation();
  const [i, setI] = useState(start);
  const f = list[i];
  useEffect(() => {
    const key = (e) => {
      if (e.key === 'Escape') { e.stopImmediatePropagation(); onClose(); }
      if (e.key === 'ArrowRight') setI((x) => Math.min(list.length - 1, x + 1));
      if (e.key === 'ArrowLeft') setI((x) => Math.max(0, x - 1));
    };
    document.addEventListener('keydown', key, true);
    return () => document.removeEventListener('keydown', key, true);
  }, [list.length, onClose]);
  if (!f) return null;
  return ReactDOM.createPortal(
    <div className="gc-viewer" role="dialog" aria-modal="true" aria-label={f.name} data-gc-viewer="1" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="gc-viewer-bar">
        <span className="gc-viewer-name" title={f.name}>{f.name}{list.length > 1 ? ` · ${i + 1}/${list.length}` : ''}</span>
        <a className="gc-viewer-btn" href={`${API}/files/${f.id}?dl=1`} data-gc-viewer-dl="1"><Download size={16} /> {t('xGcc.download')}</a>
        <button type="button" className="gc-viewer-btn" onClick={onClose} aria-label={t('xGcc.close')} data-gc-viewer-close="1"><X size={18} /></button>
      </div>
      {i > 0 && <button type="button" className="gc-viewer-nav prev" onClick={() => setI(i - 1)} aria-label={t('xGcc.prevImage')}><ChevronLeft size={28} /></button>}
      <img className="gc-viewer-img" src={`${API}/files/${f.id}`} alt={f.name} data-gc-viewer-img={f.id} />
      {i < list.length - 1 && <button type="button" className="gc-viewer-nav next" onClick={() => setI(i + 1)} aria-label={t('xGcc.nextImage')}><ChevronRight size={28} /></button>}
    </div>,
    document.body,
  );
}

/* a message's images (shown; a click opens them in the viewer above) and files (a chip to download) */
const Files = ({ list }) => {
  const [open, setOpen] = useState(null);
  if (!list || !list.length) return null;
  const images = list.filter((f) => f.image);
  return (
    <div className="gc-files">
      {list.map((f) => (f.image ? (
        <button type="button" key={f.id} className="gc-img" title={f.name} onClick={() => setOpen(images.indexOf(f))}>
          <img src={`${API}/files/${f.id}`} alt={f.name} loading="lazy" data-gc-img={f.id} />
        </button>
      ) : (
        <a key={f.id} href={`${API}/files/${f.id}?dl=1`} className="gc-file" title={f.name} data-gc-file={f.id}>
          <FileText size={16} /><span>{f.name}</span><em>{fileSize(f.bytes)}</em>
        </a>
      )))}
      {open !== null && <ImageViewer list={images} start={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

/* a small panel of emojis, floating at a fixed place (from the button), above everything; closes on a click outside */
function EmojiPop({ anchor, list, onPick, onClose, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    const down = (e) => { if (!ref.current?.contains(e.target) && !anchor?.contains(e.target)) onClose(); };
    const key = (e) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); onClose(); } };
    document.addEventListener('mousedown', down); window.addEventListener('keydown', key, true);
    return () => { document.removeEventListener('mousedown', down); window.removeEventListener('keydown', key, true); };
  }, [anchor, onClose]);
  if (!anchor) return null;
  const r = anchor.getBoundingClientRect();
  const wide = list.length > 10;
  const w = wide ? 296 : 8 * 34 + 12;
  const left = Math.max(8, Math.min(window.innerWidth - w - 8, r.right - w));
  return (
    <div ref={ref} className={`gc-emoji-pop${wide ? ' wide' : ''}`} style={{ left, bottom: Math.round(window.innerHeight - r.top + 6), width: w }} data-gc-emojipop={testid}>
      {list.map((e) => <button key={e} type="button" onClick={() => onPick(e)} data-gc-emoji={e}>{e}</button>)}
    </div>
  );
}

const Sources = ({ list }) => {
  const { t } = useTranslation();
  if (!list || !list.length) return null;
  return (
    <div className="gc-sources">
      <span>{t('xGcc.sources')}:</span>
      {list.map((s) => <span key={s.label} className="gc-src"><b>{s.label}</b> {s.title}{s.version ? ` v${s.version}` : ''} <em>{s.teamName}</em></span>)}
    </div>
  );
};

/* one dialog for every question this screen asks; its own layer above the portal's header and the page */
function Dialog({ title, children, onClose, footer }) {
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="gc-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="gc-dialog" role="dialog" aria-modal="true" aria-label={title}>
        <div className="gc-dlg-head"><h3>{title}</h3><button type="button" className="gc-x" onClick={onClose} aria-label="Close"><X size={18} /></button></div>
        <div className="gc-dlg-body">{children}</div>
        <div className="gc-dlg-foot">{footer}</div>
      </div>
    </div>
  );
}

/* QUICK ADD (Max, 27/10: "thêm trường hợp add entity, department để add nhanh 1 group", then "ko phải mà dropdown những
   được chọn cùng lúc nhiều lựa chọn"): a dropdown per kind, each opening a list of checkboxes — several can be ticked
   while it stays open. A box is ticked when all the group's people (not in the room yet) are picked, half-ticked when
   some are; ticking it picks them all, unticking drops them all. The list floats above the dialog (fixed, from the
   button's place), so the dialog's own scrolling cannot clip it; it closes on a click outside, Escape or a scroll. */
function GroupDrop({ label, groups, membersOf, picked, onToggle, testid }) {
  const { t } = useTranslation();
  const [at, setAt] = useState(null);
  const btn = useRef(null);
  const pop = useRef(null);
  useEffect(() => {
    if (!at) return undefined;
    const down = (e) => { if (!pop.current?.contains(e.target) && !btn.current?.contains(e.target)) setAt(null); };
    const key = (e) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); setAt(null); } };
    const scroll = (e) => { if (!pop.current?.contains(e.target)) setAt(null); };
    document.addEventListener('mousedown', down);
    window.addEventListener('keydown', key, true);
    window.addEventListener('scroll', scroll, true);
    return () => { document.removeEventListener('mousedown', down); window.removeEventListener('keydown', key, true); window.removeEventListener('scroll', scroll, true); };
  }, [at]);
  const rows = groups.map((g) => {
    const ids = membersOf(g.key).map((p) => p.id);
    const n = ids.filter((id) => picked.has(id)).length;
    return { ...g, ids, all: n > 0 && n === ids.length, some: n > 0 && n < ids.length };
  });
  const on = rows.filter((r) => r.all);
  const open = () => {
    if (at) { setAt(null); return; }
    const r = btn.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - 12;
    setAt({ left: Math.round(r.left), width: Math.round(Math.max(r.width, 260)),
      ...(below < 220 ? { bottom: Math.round(window.innerHeight - r.top + 4) } : { top: Math.round(r.bottom + 4) }), max: Math.max(160, Math.min(320, below < 220 ? r.top - 16 : below)) });
  };
  if (!groups.length) return null;
  return (
    <>
      <button ref={btn} type="button" className={`gc-drop ${on.length ? 'has' : ''}`} onClick={open} aria-expanded={!!at} data-gc-drop={testid}>
        <span className="gc-drop-l">{label}</span>
        <span className="gc-drop-v">{on.length ? on.map((r) => r.label || r.name).join(', ') : t('xGcc.dropNone')}</span>
        <span className="gc-drop-caret" aria-hidden="true">▾</span>
      </button>
      {at && (
        <div ref={pop} className="gc-pop" style={{ left: at.left, width: at.width, top: at.top, bottom: at.bottom, maxHeight: at.max }} role="listbox" aria-multiselectable="true" data-gc-pop={testid}>
          {rows.map((r) => (
            <label key={r.key} className={`gc-pop-opt ${r.all ? 'on' : ''}`} title={r.title || r.name} data-gc-opt={r.key}>
              <input type="checkbox" checked={r.all} ref={(el) => { if (el) el.indeterminate = r.some; }} onChange={() => onToggle(r.ids, r.all)} />
              <span>{r.label ? <><b>{r.label}</b> {r.title}</> : r.name}</span>
              <em>{r.ids.length}</em>
            </label>
          ))}
        </div>
      )}
    </>
  );
}

function PeoplePicker({ people, groups, exclude, picked, setPicked }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const free = people.filter((p) => !exclude.has(p.id));
  const inTeam = (k) => free.filter((p) => (p.teams || []).includes(k));
  const inEntity = (k) => free.filter((p) => p.entity === k);
  const toggle = (ids, off) => setPicked((s) => { const n = new Set(s); ids.forEach((id) => (off ? n.delete(id) : n.add(id))); return n; });
  const teams = (groups.teams || []).filter((g) => inTeam(g.key).length);
  const entities = (groups.entities || []).filter((g) => inEntity(g.key).length).map((g) => ({ ...g, label: g.key, title: g.name }));
  const shown = free
    .filter((p) => !q || `${p.name} ${p.email}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (picked.has(b.id) - picked.has(a.id)) || a.name.localeCompare(b.name));
  return (
    <div className="gc-picker">
      <div className="gc-quick">
        <span className="gc-muted">{t('xGcc.quickAdd')}</span>
        <GroupDrop label={t('xGcc.chipDepts')} groups={teams} membersOf={inTeam} picked={picked} onToggle={toggle} testid="dept" />
        <GroupDrop label={t('xGcc.chipEntities')} groups={entities} membersOf={inEntity} picked={picked} onToggle={toggle} testid="entity" />
      </div>
      <div className="gc-picker-bar">
        <input type="search" className="pa-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('xGcc.searchPeople')} data-gc-peoplesearch />
        <span className="gc-muted">{t('xGcc.picked', { n: picked.size })}</span>
        {picked.size > 0 && <button type="button" className="pa-btn gc-clear" onClick={() => setPicked(new Set())} data-gc-clearpicked>{t('xGcc.clearPicked')}</button>}
      </div>
      <div className="gc-picker-list">
        {shown.map((p) => (
          <label key={p.id} className={`gc-person ${picked.has(p.id) ? 'on' : ''}`}>
            <input type="checkbox" checked={picked.has(p.id)} onChange={() => toggle([p.id], picked.has(p.id))} />
            <UserAvatar url={p.avatar} name={p.name} size={26} />
            <span className="gc-person-name">{p.name}<em>{p.email}</em></span>
          </label>
        ))}
      </div>
    </div>
  );
}

/* THE GUIDE (Max 01/11: every module has one). The page opens with no room chosen unless the address names one, so the
   first steps are the room list and the empty state; the steps inside a room (people, switches, thread, composer, Catchy's
   side window) are skipped by TourGuide until a room is open — the guide does not pick a room for anybody, because opening
   one marks it read. */
const TOUR = [
  { selector: '[data-gc-rooms]', title: 'tourGcc.roomsT', body: 'tourGcc.roomsB' },
  { selector: '[data-gc-new]', title: 'tourGcc.newT', body: 'tourGcc.newB' },
  { selector: '.gc-empty', title: 'tourGcc.pickT', body: 'tourGcc.pickB' },
  { selector: '[data-gc-members]', title: 'tourGcc.membersT', body: 'tourGcc.membersB' },
  { selector: '.gc-catchy-sw', title: 'tourGcc.catchyT', body: 'tourGcc.catchyB' },
  { selector: '.gc-tr-sw', title: 'tourGcc.trT', body: 'tourGcc.trB' },
  { selector: '[data-gc-thread]', title: 'tourGcc.threadT', body: 'tourGcc.threadB' },
  { selector: '.gc-compose', title: 'tourGcc.composeT', body: 'tourGcc.composeB' },
];

export default function Gcc() {
  const { t } = useTranslation();
  const me = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const roomPk = Number(new URLSearchParams(location.search).get('room')) || null;
  const setRoom = (pk) => navigate(pk ? `/gcc?room=${pk}` : '/gcc', { replace: true });

  const [rooms, setRooms] = useState(null);
  const [people, setPeople] = useState([]);
  const [groups, setGroups] = useState({ entities: [], teams: [] });
  const [room, setRoomData] = useState(null);
  const [members, setMembers] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [catchy, setCatchy] = useState({ answering: false, watching: false });
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [dlg, setDlg] = useState(null);
  const [busy, setBusy] = useState(false);
  /* REPLY and EDIT (Max 30/10: "GCC thiếu tính năng reply và edit lại tin nhắn"): the message being answered, the one being
     edited, and the server time of the last poll — edits of older messages come back with each poll from then on */
  const [replyTo, setReplyTo] = useState(null);      // the message object being answered
  const [editing, setEditing] = useState(null);      // { msgPk, text, busy }
  const editSince = useRef(null);
  const [reacts, setReacts] = useState([]);
  const [pending, setPending] = useState([]);      // files waiting to go with the next message
  const [emojiAt, setEmojiAt] = useState(null);    // the compose emoji panel's button
  const [reactAt, setReactAt] = useState(null);    // { el, msgPk } — the quick reactions of one message
  const [tagQ, setTagQ] = useState(null);          // { q, start, i } while "@…" is being typed
  const tagged = useRef(new Map());                // the people picked from the @ list for this message: name → id
  const [trAll, setTrAll] = useState(false);       // "Translate the messages above" asked in this visit
  const lastPk = useRef(0);
  const readPk = useRef(0);
  const threadRef = useRef(null);
  const sideRef = useRef(null);
  const inputRef = useRef(null);

  const loadRooms = useCallback(() => call('GET', '/rooms').then((r) => setRooms(r.rooms)).catch((e) => setErr(e.message)), []);
  useEffect(() => {
    loadRooms();
    call('GET', '/people').then((r) => { setPeople(r.people); setGroups({ entities: r.entities || [], teams: r.teams || [] }); }).catch(() => {});
    const id = setInterval(() => { if (!document.hidden) loadRooms(); }, ROOMS_MS);
    return () => clearInterval(id);
  }, [loadRooms]);

  /* the open room: everything once, then only what is new */
  const firstPk = useRef(0);
  const trAllRef = useRef(false);
  const pull = useCallback(async (pk, fresh) => {
    // the reader's language rides along, so a message already translated on send arrives with its translation (28/10)
    const lang = readLang();
    const es = !fresh && editSince.current ? `&esince=${encodeURIComponent(editSince.current)}` : '';
    const r = await call('GET', `/rooms/${pk}/messages?after=${fresh ? 0 : lastPk.current}&lang=${lang}&rfrom=${fresh ? 0 : firstPk.current}${trAllRef.current ? '&all=1' : ''}${es}`);
    if (r.now) editSince.current = r.now;
    if ((r.edits || []).length) {
      const by = new Map(r.edits.map((x) => [x.msgPk, x]));
      setMsgs((all) => all.map((m) => (by.has(m.msg_pk) ? { ...m, body: by.get(m.msg_pk).body, edited_at: by.get(m.msg_pk).editedAt, mentions: by.get(m.msg_pk).mentions, tr: null,
        ...(by.get(m.msg_pk).deleted ? { deleted: true, files: [] } : {}) } : m)));
    }
    (r.messages || []).forEach((m) => { m.trLang = lang; });
    if (fresh && r.messages.length) firstPk.current = r.messages[0].msg_pk;
    setRoomData(r.room); setMembers(r.members); setCatchy(r.catchy); setReacts(r.reactions || []);
    if (fresh) setMsgs(r.messages); else if (r.messages.length) setMsgs((m) => [...m, ...r.messages.filter((x) => !m.some((y) => y.msg_pk === x.msg_pk))]);
    if (r.messages.length) lastPk.current = Math.max(lastPk.current, ...r.messages.map((x) => x.msg_pk));
  }, []);
  useEffect(() => {
    if (!roomPk) { setRoomData(null); setMsgs([]); return undefined; }
    lastPk.current = 0; readPk.current = 0; firstPk.current = 0; editSince.current = null; setReplyTo(null); setEditing(null); trAllRef.current = false; setTrAll(false); setMsgs([]); setRoomData(null); setPending([]); setReacts([]);
    pull(roomPk, true).catch((e) => { setErr(e.message); setRoom(null); });
    const id = setInterval(() => { if (!document.hidden) pull(roomPk, false).catch(() => {}); }, POLL_MS);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomPk, pull]);

  const main = useMemo(() => msgs.filter((m) => m.kind !== 'side'), [msgs]);
  const side = useMemo(() => msgs.filter((m) => m.kind === 'side'), [msgs]);
  /* EVERY MESSAGE IN THE READER'S PORTAL LANGUAGE (Max, 28/10: "chuyển ngữ trực tiếp theo system language … các câu trả lời
     của catchy hay ai đó cũng chuyển về theo đúng ngôn ngữ của hệ thống") — what anybody wrote, Catchy's answers and its
     live posts, the reader's own too; AI Hub's translator, cached per text and language. A message already in the
     reader's language is shown as written; a translated one says so and can be read as it was typed. */
  const { i18n: i18nNow } = useTranslation();
  /* THIS READER'S SWITCH (Jay 29/10): off — not one translation is asked for; on — the messages since it was turned on, and the
     ones above only once they ask ("Translate the messages above") */
  const trOn = room ? room.trOn !== false : false;
  // on = every message, the older ones too (Jay 30/10: re-opening the room showed the originals again)
  const trWants = () => trOn;
  /* INTERPRETED WITH THE CHAT AS CONTEXT (Jay #1171): a message that came without one in this reader's language (an older one, or
     one sent before anybody read this language) is asked for by its id, so the server reads the messages before it — not the
     bare text, word by word. The server joins an interpretation already being made for the same message. */
  const [own, setOwn] = useState({});
  const trId = (lang, m) => `${lang}|${m.msg_pk}|${m.edited_at || ''}`;   // an edited message is a new text: asked again
  const askedTr = useRef(new Set());
  useEffect(() => {
    if (!roomPk || !trOn) return;
    const lang = readLang();
    const want = msgs.filter((m) => m.kind !== 'system' && m.body && !(m.tr && m.trLang === lang) && !plainlyIn(lang, m.body)
      && !askedTr.current.has(trId(lang, m))).reverse();   // newest first: what is on screen
    if (!want.length) return;
    want.forEach((m) => askedTr.current.add(trId(lang, m)));
    const parts = [];
    for (let i = 0; i < want.length; i += 6) parts.push(want.slice(i, i + 6));
    let next = 0;
    const worker = async () => {
      while (next < parts.length) {
        const part = parts[next]; next += 1;
        try {
          // eslint-disable-next-line no-await-in-loop
          const r = await call('POST', `/rooms/${roomPk}/interpret`, { lang, msgs: part.map((m) => m.msg_pk) });
          setOwn((o) => { const n = { ...o }; part.forEach((m) => { if (r.out?.[m.msg_pk]) n[trId(lang, m)] = r.out[m.msg_pk]; }); return n; });
        } catch (e) { part.forEach((m) => askedTr.current.delete(trId(lang, m))); }
      }
    };
    for (let k = 0; k < Math.min(3, parts.length); k += 1) worker();
  }, [msgs, roomPk, trOn, i18nNow.language, trAll]); // eslint-disable-line react-hooks/exhaustive-deps
  const trOf = (m) => {
    const lang = readLang();
    if (m.tr && m.trLang === lang) return { tr: m.tr, unclear: !!m.trUnclear };
    return own[trId(lang, m)] || null;
  };
  const [orig, setOrig] = useState(() => new Set());
  /* A render FUNCTION, not a component declared in here: a component made inside this one is a new type on every render,
     so each poll re-mounted every message — an open picture viewer closed itself (Jay 29/10), as the reaction tray had. */
  const said = (m) => {
    if (m.deleted) return <em className="gc-deleted" data-gc-deleted={m.msg_pk}>{t('xGcc.msgDeleted')}</em>;
    const got = trWants(m) ? trOf(m) : null;
    const v = got?.tr || null;
    const showOrig = orig.has(m.msg_pk);
    return (
      <>
        {m.body ? <Text text={v && !showOrig ? v : m.body} tags={m.mentions} meId={me.userId} /> : null}
        <Files list={m.files} />
        {v && (
          <button type="button" className="gc-trnote" data-gc-tr={showOrig ? 'orig' : 'tr'}
            onClick={() => setOrig((o) => { const n = new Set(o); if (n.has(m.msg_pk)) n.delete(m.msg_pk); else n.add(m.msg_pk); return n; })}>
            {showOrig ? t('xGcc.showTranslation') : t('xGcc.translated')}
            {/* the original itself was unclear — the interpretation kept it so rather than guess (#1171) */}
            {!showOrig && got?.unclear && <span className="gc-trunclear" title={t('xGcc.trUnclear')} data-gc-trunclear> · {t('xGcc.trUnclearShort')}</span>}
          </button>
        )}
      </>
    );
  };
  const sideOpen = !!room?.sideOpen;
  /* Catchy itself on or off for the room (Jay, 28/10) — off: it reads nothing and answers nothing */
  const catchyOn = room ? room.catchyOn !== false : true;
  const asksCatchyAsleep = !catchyOn && /@catchy\b/i.test(text);

  /* new messages: to the bottom, and marked read while the tab is in view */
  useEffect(() => { const el = threadRef.current; if (el) el.scrollTop = el.scrollHeight; }, [main.length, catchy.answering]);
  useEffect(() => { const el = sideRef.current; if (el) el.scrollTop = el.scrollHeight; }, [side.length, sideOpen, catchy.watching]);
  /* Max 30/10: the menu kept "2" while he was looking at the room — messages that came while the tab was hidden were never
     marked read when he came back (only a NEW message re-ran this), and the menu learnt of a read only a minute later.
     Now coming back to the tab marks them read too, and the menu row is told at once (event gcc-read → Header). */
  const markRead = useCallback(() => {
    if (!roomPk || !lastPk.current || document.hidden || lastPk.current <= readPk.current) return;
    readPk.current = lastPk.current;
    call('POST', `/rooms/${roomPk}/read`, { msgPk: lastPk.current })
      .then(() => { window.dispatchEvent(new Event('gcc-read')); return loadRooms(); }).catch(() => {});
  }, [roomPk, loadRooms]);
  useEffect(() => { markRead(); }, [msgs.length, markRead]);
  useEffect(() => {
    const back = () => { if (!document.hidden) markRead(); };
    document.addEventListener('visibilitychange', back); window.addEventListener('focus', back);
    return () => { document.removeEventListener('visibilitychange', back); window.removeEventListener('focus', back); };
  }, [markRead]);

  /* the files go up first, one by one (raw bytes), then the message that carries them */
  const upload = async (f) => {
    const r = await fetch(`${API}/rooms/${roomPk}/files?name=${encodeURIComponent(f.file.name || 'screenshot.png')}&type=${encodeURIComponent(f.file.type || '')}`, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/octet-stream' }, body: f.file });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
    return j.id;
  };
  const sendMsg = async () => {
    const body = text.trim();
    if ((!body && !pending.length) || !roomPk || busy) return;
    setBusy(true); setErr('');
    try {
      const files = [];
      for (const f of pending) files.push(f.id || await upload(f)); // eslint-disable-line no-await-in-loop
      // the people picked from the list whose "@Name" is still in the text
      const mentions = [...tagged.current.entries()].filter(([n]) => body.includes(`@${n}`)).map(([, id]) => id);
      await call('POST', `/rooms/${roomPk}/messages`, { body, files, mentions, replyTo: replyTo ? replyTo.msg_pk : null });
      setText(''); setReplyTo(null); pending.forEach((f) => f.url && URL.revokeObjectURL(f.url)); setPending([]); tagged.current = new Map();
      await pull(roomPk, false); loadRooms();
    } catch (e) { setErr(e.message); } finally { setBusy(false); inputRef.current?.focus(); }
  };
  const saveEdit = async () => {
    if (!editing || editing.busy) return;
    const body = editing.text.trim();
    if (!body) { setErr(t('xGcc.emptyEdit')); return; }
    setEditing((x) => ({ ...x, busy: true }));
    try {
      await call('PUT', `/rooms/${roomPk}/messages/${editing.msgPk}`, { body });
      setMsgs((all) => all.map((m) => (m.msg_pk === editing.msgPk ? { ...m, body, edited_at: new Date().toISOString(), tr: null } : m)));
      setEditing(null);
      loadRooms();   // the room list's last-line preview
    } catch (e) { setErr(e.message); setEditing((x) => (x ? { ...x, busy: false } : x)); }
  };
  const jumpTo = (pk) => {
    const el = document.querySelector(`[data-gc-msg="${pk}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('gc-flash'); setTimeout(() => el.classList.remove('gc-flash'), 1600);
  };
  const addFiles = (list) => {
    const add = [...list].filter(Boolean).map((file) => {
      if (file.size > 20 * 1024 * 1024) { setErr(t('xGcc.fileTooBig', { name: file.name })); return null; }
      const isImg = /^image\/(png|jpeg|gif|webp)$/.test(file.type);
      return { key: `${Date.now()}-${Math.random()}`, file, url: isImg ? URL.createObjectURL(file) : null };
    }).filter(Boolean);
    if (add.length) setPending((p) => [...p, ...add].slice(0, 10));
  };
  const onPaste = (e) => {
    const files = [...(e.clipboardData?.items || [])].filter((x) => x.kind === 'file').map((x) => x.getAsFile()).filter(Boolean);
    if (files.length) { e.preventDefault(); addFiles(files.map((f, i) => (f.name && f.name !== 'image.png' ? f
      : new File([f], `screenshot-${new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '')}${i ? `-${i}` : ''}.png`, { type: f.type || 'image/png' })))); }
  };
  const react = async (msgPk, emoji) => {
    setReactAt(null);
    try { await call('POST', `/rooms/${roomPk}/messages/${msgPk}/react`, { emoji }); await pull(roomPk, false); } catch (e) { setErr(e.message); }
  };
  const reactsOf = useMemo(() => { const m = new Map(); reacts.forEach((x) => { const a = m.get(x.msgPk) || []; a.push(x); m.set(x.msgPk, a); }); return m; }, [reacts]);
  /* A FUNCTION, not a component declared here (Jay 29/10: "Emoji tray appears and then disappear when mouse goes away"): as
     a component it was a new type on every render, so each poll re-created the + button, the tray lost the element it
     hangs from and went off-screen. The tray now finds its button by data attribute, and the button stays shown while
     its tray is open. */
  const renderReacts = (m) => (m.deleted ? null : (
    <div className="gc-reacts" data-gc-reacts={m.msg_pk}>
      {(reactsOf.get(m.msg_pk) || []).map((x) => (
        <button key={x.emoji} type="button" className={`gc-react${x.mine ? ' mine' : ''}`} title={x.names} onClick={() => react(m.msg_pk, x.emoji)} data-gc-react={x.emoji}>
          {x.emoji}<b>{x.n}</b>
        </button>
      ))}
      <button type="button" className={`gc-react-add${reactAt && reactAt.msgPk === m.msg_pk ? ' open' : ''}`} title={t('xGcc.react')} aria-label={t('xGcc.react')}
        onClick={() => setReactAt(reactAt && reactAt.msgPk === m.msg_pk ? null : { msgPk: m.msg_pk })} data-gc-reactadd={m.msg_pk}><SmilePlus size={14} /></button>
      <button type="button" className="gc-react-add gc-act" title={t('xGcc.reply')} aria-label={t('xGcc.reply')}
        onClick={() => { setReplyTo(m); inputRef.current?.focus(); }} data-gc-reply={m.msg_pk}><Reply size={14} /></button>
      {m.kind === 'user' && Number(m.user_id) === Number(me.userId) && (
        <button type="button" className="gc-react-add gc-act" title={t('xGcc.edit')} aria-label={t('xGcc.edit')}
          onClick={() => setEditing({ msgPk: m.msg_pk, text: m.body || '' })} data-gc-edit={m.msg_pk}><Pencil size={13} /></button>
      )}
      {/* delete one's own message (Max 04/11, FB-0002) — asked first */}
      {m.kind === 'user' && Number(m.user_id) === Number(me.userId) && (
        <button type="button" className="gc-react-add gc-act gc-act-del" title={t('xGcc.deleteMsg')} aria-label={t('xGcc.deleteMsg')}
          onClick={() => setDlg({ kind: 'delmsg', msgPk: m.msg_pk })} data-gc-delmsg={m.msg_pk}><Trash2 size={13} /></button>
      )}
    </div>
  ));
  /* WHO HAS SEEN IT (Max 04/11, with Amy's FB-0002: "hiện ai đã xem tin nhắn"): each member's face under the last message they have
     read, Google Chat's way — the server sends how far each has read with the room */
  const seenAt = useMemo(() => {
    const out = new Map();
    const shown = msgs.filter((m) => m.kind === 'user' || m.kind === 'catchy').map((m) => m.msg_pk).sort((a, b) => a - b);
    members.forEach((p) => {
      if (Number(p.id) === Number(me.userId) || !p.lastRead) return;
      let at = null;
      for (const pk of shown) { if (pk <= p.lastRead) at = pk; else break; }
      if (at) out.set(at, [...(out.get(at) || []), p]);
    });
    return out;
  }, [msgs, members, me.userId]);
  const renderSeen = (m) => {
    const who = seenAt.get(m.msg_pk);
    if (!who) return null;
    return (
      <div className="gc-seen" title={t('xGcc.seenBy', { names: who.map((p) => p.name).join(', ') })} data-gc-seen={m.msg_pk}>
        {who.slice(0, 8).map((p) => <UserAvatar key={p.id} url={p.avatar} name={p.name} size={16} />)}{who.length > 8 && <span>+{who.length - 8}</span>}
      </div>
    );
  };
  /* the quote over a reply (a person's — a Catchy answer already sits under its question) and the time line with "edited" */
  const renderQuote = (m) => (m.kind === 'user' && m.reply ? (
    <button type="button" className="gc-quote" onClick={() => jumpTo(m.reply.msgPk)} title={t('xGcc.jumpTo')} data-gc-quote={m.reply.msgPk}>
      <Reply size={12} /> <b>{m.reply.user || '—'}</b> <span>{plainText(m.reply.body).replace(/\s+/g, ' ').slice(0, 120) || '📎'}</span>
    </button>
  ) : null);
  const renderTime = (m) => <div className="gc-time">{when(m.created_at)}{m.edited_at && <em className="gc-edited" title={when(m.edited_at)}> · {t('xGcc.edited')}</em>}</div>;
  const renderEdit = () => (
    <div className="gc-editbox" data-gc-editbox>
      {/* as tall as the message (a long line wraps; counting line breaks gave a two-line box over a six-line message) */}
      <textarea value={editing.text} rows={2} autoFocus data-gc-edittext
        ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight + 2, 320)}px`; } }}
        onChange={(e) => setEditing((x) => ({ ...x, text: e.target.value }))}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); setEditing(null); }
          if (enterSends(e)) { e.preventDefault(); saveEdit(); }
        }} />
      <div className="gc-editbtns">
        <span className="gc-muted">{t('xGcc.editHint')}</span>
        <button type="button" className="pa-btn" onClick={() => setEditing(null)}>{t('xGcc.cancel')}</button>
        <button type="button" className="pa-btn primary" onClick={saveEdit} disabled={editing.busy} data-gc-editsave>{t('xGcc.save')}</button>
      </div>
    </div>
  );
  /* @ — the room's people (and Catchy) as you type; the pick goes in as "@Full Name" and is sent as a tag */
  const tagChoices = useMemo(() => {
    if (!tagQ) return [];
    const q = fold(tagQ.q);
    const ppl = members.filter((m) => Number(m.id) !== Number(me.userId))
      .filter((m) => !q || fold(m.name).split(' ').some((w) => w.startsWith(q)) || fold(m.name).startsWith(q))
      .map((m) => ({ id: m.id, name: m.name, avatar: m.avatar }));
    const bot = catchyOn && (!q || 'catchy'.startsWith(q)) ? [{ id: 'catchy', name: 'Catchy' }] : [];
    // @all rings everybody in the room (gcc.js mentionsIn; Max 02/11) — read from the text, so it is not a tag
    const all = members.length > 1 && (!q || 'all'.startsWith(q)) ? [{ id: 'all', name: 'all', label: t('xGcc.tagAll') }] : [];
    return [...all, ...ppl, ...bot].slice(0, 8);
  }, [tagQ, members, me.userId, catchyOn, t]);
  const watchTag = (value, caret) => {
    const m = value.slice(0, caret).match(/(^|\s)@([\p{L}\p{N}._-]*)$/u);
    setTagQ(m ? { q: m[2], start: caret - m[2].length - 1, i: 0 } : null);
  };
  const pickTag = (p) => {
    if (!tagQ) return;
    const el = inputRef.current;
    const caret = el ? el.selectionStart : text.length;
    const ins = `@${p.name} `;
    const next = text.slice(0, tagQ.start) + ins + text.slice(caret);
    if (p.id !== 'catchy' && p.id !== 'all') tagged.current.set(p.name, p.id);
    setText(next); setTagQ(null);
    requestAnimationFrame(() => { if (el) { el.focus(); const at = tagQ.start + ins.length; el.setSelectionRange(at, at); } });
  };
  const insertAtCaret = (x) => {
    const el = inputRef.current;
    const a = el ? el.selectionStart : text.length; const b = el ? el.selectionEnd : text.length;
    setText(text.slice(0, a) + x + text.slice(b));
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(a + x.length, a + x.length); } });
  };
  const toggleTr = async () => {
    const on = !trOn;
    setRoomData((r) => ({ ...r, trOn: on }));
    try { await call('PUT', `/rooms/${roomPk}/translate`, { on }); trAllRef.current = false; setTrAll(false); await pull(roomPk, true); }
    catch (e) { setErr(e.message); setRoomData((r) => ({ ...r, trOn: !on })); }
  };
  const translateAbove = async () => { trAllRef.current = true; setTrAll(true); try { await pull(roomPk, true); } catch (e) { setErr(e.message); } };
  const earlierUntranslated = false;   // nothing is left untranslated any more (see trWants)
  /* DESKTOP ALERTS, this browser's (gccAlerts.js): the browser asks once; a refusal is undone in its own site settings */
  const [desk, setDesk] = useState(() => desktopOn());
  const toggleDesk = async () => {
    const r = await setDesktop(!desk);
    setDesk(r === 'on');
    if (r === 'denied') setErr(t('xGcc.deskDenied'));
    if (r === 'unsupported') setErr(t('xGcc.deskUnsupported'));
  };
  /* SCHEDULED SEND (Anh Le 02/11): the text in the box goes out at the time picked; the writer's own list of what waits */
  const [sched, setSched] = useState([]);
  const [schedAt, setSchedAt] = useState(null);       // the popover's anchor
  const [schedOpen, setSchedOpen] = useState(false);  // the list folded / open
  const [custom, setCustom] = useState('');
  const loadSched = useCallback(() => (roomPk ? call('GET', `/rooms/${roomPk}/scheduled`).then((r) => setSched(r.scheduled || [])).catch(() => setSched([])) : null), [roomPk]);
  useEffect(() => { setSched([]); loadSched(); const h = setInterval(loadSched, 60000); return () => clearInterval(h); }, [loadSched]);
  const presets = () => {
    const now = new Date();
    const at = (d, h) => { const x = new Date(d); x.setHours(h, 0, 0, 0); return x; };
    const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
    const monday = new Date(now); monday.setDate(now.getDate() + (((8 - now.getDay()) % 7) || 7));
    return [
      { k: 'tm8', label: t('xGcc.schedTomorrowAt', { time: fmtTime(at(tomorrow, 8)) }), at: at(tomorrow, 8) },
      { k: 'tm13', label: t('xGcc.schedTomorrowAt', { time: fmtTime(at(tomorrow, 13)) }), at: at(tomorrow, 13) },
      { k: 'mon8', label: t('xGcc.schedMondayAt', { time: fmtTime(at(monday, 8)) }), at: at(monday, 8) },
    ];
  };
  const fmtTime = (d) => d.toLocaleTimeString(i18n.language || 'en', { hour: 'numeric', minute: '2-digit' });
  const [custOpen, setCustOpen] = useState(false);
  const fmtAt = (d) => new Date(d).toLocaleString(dateLocale(i18n.language), { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  const scheduleMsg = async (at) => {
    const body = text.trim();
    if ((!body && !pending.length) || !roomPk || busy) return;
    setBusy(true); setErr('');
    try {
      const files = [];
      for (const f of pending) files.push(f.id || await upload(f)); // eslint-disable-line no-await-in-loop
      const mentions = [...tagged.current.entries()].filter(([n]) => body.includes(`@${n}`)).map(([, id]) => id);
      await call('POST', `/rooms/${roomPk}/scheduled`, { body, files, mentions, replyTo: replyTo ? replyTo.msg_pk : null, sendAt: new Date(at).toISOString() });
      setText(''); setReplyTo(null); pending.forEach((f) => f.url && URL.revokeObjectURL(f.url)); setPending([]); tagged.current = new Map();
      setSchedAt(null); setCustom(''); setSchedOpen(true); await loadSched();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const schedAct = async (id, how, extra) => {
    setErr('');
    try {
      if (how === 'send') { await call('POST', `/scheduled/${id}/send`); await pull(roomPk, false); loadRooms(); }
      else if (how === 'cancel') await call('DELETE', `/scheduled/${id}`);
      else if (how === 'move') await call('PUT', `/scheduled/${id}`, { sendAt: new Date(extra).toISOString() });
      await loadSched();
    } catch (e) { setErr(e.message); }
  };
  /* FORMATTING (Max 02/11): the "A" opens a bar, as in Google Chat; each button writes the marks Text reads, around the
     selection (or the line, for a list) — pressed again on marked text, it takes them off */
  const [fmtOn, setFmtOn] = useState(false);
  // the caret goes where the edit left it in the same paint as the new text — a frame later, a fast typist's next key landed
  // at the end of the box
  const caretAt = useRef(null);
  const caretTo = (a, b) => { caretAt.current = [a, b]; };
  useLayoutEffect(() => {
    const el = inputRef.current; const c = caretAt.current;
    if (el && c) { caretAt.current = null; el.focus(); el.setSelectionRange(c[0], c[1]); }
  }, [text]);
  const wrapSel = (mark) => {
    const el = inputRef.current; if (!el) return;
    const a = el.selectionStart; const b = el.selectionEnd; const sel = text.slice(a, b); const L = mark.length;
    if (text.slice(a - L, a) === mark && text.slice(b, b + L) === mark) {
      setText(text.slice(0, a - L) + sel + text.slice(b + L)); caretTo(a - L, b - L);
    } else {
      setText(text.slice(0, a) + mark + sel + mark + text.slice(b)); caretTo(a + L, b + L);
    }
  };
  const listSel = (kind) => {
    const el = inputRef.current; if (!el) return;
    const a = text.lastIndexOf('\n', el.selectionStart - 1) + 1;
    let b = text.indexOf('\n', el.selectionEnd); if (b < 0) b = text.length;
    const lines = text.slice(a, b).split('\n');
    const strip = (l) => l.replace(/^\s*(?:[-•]|\d{1,3}[.)])\s+/, '');
    const all = lines.every((l) => (kind === 'ul' ? /^\s*[-•]\s+/ : /^\s*\d{1,3}[.)]\s+/).test(l));
    const block = (all ? lines.map(strip) : lines.map((l, i) => `${kind === 'ul' ? '- ' : `${i + 1}. `}${strip(l)}`)).join('\n');
    setText(text.slice(0, a) + block + text.slice(b)); caretTo(a + block.length, a + block.length);
  };
  // Shift+Enter inside a list starts the next item; on an empty item it ends the list
  const listEnter = (e) => {
    const el = e.currentTarget; const at = el.selectionStart;
    if (at !== el.selectionEnd) return false;
    const ls = text.lastIndexOf('\n', at - 1) + 1;
    const m = text.slice(ls, at).match(/^(\s*)([-•]|\d{1,3}[.)])(\s+)(.*)$/);
    if (!m) return false;
    e.preventDefault();
    if (!m[4].trim()) { setText(text.slice(0, ls) + text.slice(at)); caretTo(ls, ls); return true; }
    const mk = /\d/.test(m[2]) ? `${parseInt(m[2], 10) + 1}${m[2].slice(-1)}` : m[2];
    const ins = `\n${m[1]}${mk} `;
    setText(text.slice(0, at) + ins + text.slice(at)); caretTo(at + ins.length, at + ins.length);
    return true;
  };
  const FMT = [
    { k: 'b', Icon: Bold, act: () => wrapSel('**'), label: 'fmtBold', keys: 'Ctrl+B' },
    { k: 'i', Icon: Italic, act: () => wrapSel('_'), label: 'fmtItalic', keys: 'Ctrl+I' },
    { k: 'u', Icon: Underline, act: () => wrapSel('__'), label: 'fmtUnderline', keys: 'Ctrl+U' },
    { k: 's', Icon: Strikethrough, act: () => wrapSel('~~'), label: 'fmtStrike', keys: 'Ctrl+Shift+X' },
    { k: 'code', Icon: Code, act: () => wrapSel('`'), label: 'fmtCode' },
    { k: 'ul', Icon: List, act: () => listSel('ul'), label: 'fmtBullets', sep: true },
    { k: 'ol', Icon: ListOrdered, act: () => listSel('ol'), label: 'fmtNumbers' },
  ];
  const toggleCatchy = async () => {
    const on = !catchyOn;
    setRoomData((r) => ({ ...r, catchyOn: on }));
    try { await call('PUT', `/rooms/${roomPk}/catchy`, { on }); await pull(roomPk, false); loadRooms(); }
    catch (e) { setErr(e.message); setRoomData((r) => ({ ...r, catchyOn: !on })); }
  };
  /* ON A PHONE (Jay task #1172) Catchy's side window is a sheet over the room, opened from the room's head — the column and the
     rail do not fit beside the chat */
  const [sideSheet, setSideSheet] = useState(false);
  useEffect(() => { setSideSheet(false); }, [roomPk]);
  const toggleSide = async () => {
    const open = !sideOpen;
    setRoomData((r) => ({ ...r, sideOpen: open }));
    try { await call('PUT', `/rooms/${roomPk}/side`, { open }); } catch (e) { setErr(e.message); setRoomData((r) => ({ ...r, sideOpen: !open })); }
  };

  /* ---- the dialogs */
  const openNew = () => setDlg({ kind: 'new', name: '', picked: new Set() });
  const doDlg = async () => {
    setDlg((d) => ({ ...d, busy: true, err: '' }));
    try {
      if (dlg.kind === 'new') {
        const r = await call('POST', '/rooms', { name: dlg.name, members: [...dlg.picked] });
        await loadRooms(); setRoom(r.roomPk);
        setNote(r.mailed ? t('xGcc.mailed', { n: r.mailed }) : '');
      } else if (dlg.kind === 'add') {
        const r = await call('POST', `/rooms/${roomPk}/members`, { userIds: [...dlg.picked] });
        setNote(r.mailed ? t('xGcc.mailed', { n: r.mailed }) : '');
      } else if (dlg.kind === 'rename') {
        await call('PUT', `/rooms/${roomPk}`, { name: dlg.name });
      } else if (dlg.kind === 'leave') {
        await call('DELETE', `/rooms/${roomPk}/members/${me.userId}`); setRoom(null);
      } else if (dlg.kind === 'delete') {
        await call('DELETE', `/rooms/${roomPk}`); setRoom(null);
      } else if (dlg.kind === 'delmsg') {
        await call('DELETE', `/rooms/${roomPk}/messages/${dlg.msgPk}`);
        setMsgs((all) => all.map((m) => (m.msg_pk === dlg.msgPk ? { ...m, deleted: true, body: '', files: [], tr: null } : m)));
        setDlg(null);
        return;
      } else if (dlg.kind === 'members' && dlg.removing) {
        // Jay 30/10: "Please add a function to remove people in this room" — the owner removes; the server says so too
        await call('DELETE', `/rooms/${roomPk}/members/${dlg.removing.id}`);
        setDlg((d) => ({ ...d, busy: false, removing: null })); await pull(roomPk, false); loadRooms();
        return;
      }
      setDlg(null); loadRooms(); if (roomPk && (dlg.kind === 'add' || dlg.kind === 'rename')) pull(roomPk, false);
    } catch (e) { setDlg((d) => ({ ...d, busy: false, err: e.message })); }
  };
  const setPicked = (fn) => setDlg((d) => ({ ...d, picked: typeof fn === 'function' ? fn(d.picked) : fn }));
  const memberIds = useMemo(() => new Set(members.map((m) => m.id)), [members]);

  const byId = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const mine = (m) => Number(m.user_id) === Number(me.userId);

  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main gc-page">
        <div className="mh-titlerow">
          <div className="pa-title gc-title">
            <h2>{t('xGcc.title')}</h2>
            <p>{t('xGcc.sub')}</p>
          </div>
          <div className="gc-title-btns">
            {desktopSupported() && (
              <button type="button" className={`pa-btn gc-desk${desk ? ' on' : ''}`} onClick={toggleDesk} title={t('xGcc.deskHint')} data-gc-desk>
                <MonitorUp size={14} /> {desk ? t('xGcc.deskOn') : t('xGcc.deskOff')}
              </button>
            )}
            <ModuleHelp steps={TOUR} tourKey="gcc" ready={rooms !== null} />
          </div>
        </div>
        {err && <div className="eu-flash bad">{err}</div>}
        {/* the invitations went by mail (Max, 27/10: "thêm gửi email khi invite") */}
        {note && <div className="eu-flash ok gc-note" data-gc-note>{note}<button type="button" className="gc-x" onClick={() => setNote('')} aria-label="Close"><X size={15} /></button></div>}
        <div className={`gc-grid ${room ? (sideOpen ? 'with-side' : 'with-rail') : ''}${roomPk ? ' has-room' : ''}`}>
          {/* ---- rooms */}
          <aside className="gc-rooms" data-gc-rooms>
            <button type="button" className="pa-btn primary gc-new" onClick={openNew} data-gc-new><Plus size={15} /> {t('xGcc.newRoom')}</button>
            <div className="gc-label">{t('xGcc.rooms')}</div>
            {rooms && !rooms.length && <p className="gc-muted">{t('xGcc.noRooms')}</p>}
            {(rooms || []).map((r) => (
              <button key={r.room_pk} type="button" className={`gc-room ${r.room_pk === roomPk ? 'on' : ''}`} onClick={() => setRoom(r.room_pk)} data-gc-room={r.room_pk}>
                <span className="gc-room-top"><b>{r.name}</b>{r.catchy_on === false && <span className="gc-room-zz" title={t('xGcc.offTitle')}>💤</span>}{r.unread > 0 && r.room_pk !== roomPk && <span className="gc-badge">{r.unread}</span>}</span>
                <span className="gc-room-last">{r.last_kind === 'catchy' ? 'Catchy: ' : r.last_by ? `${r.last_by}: ` : ''}{plainText(r.last_body).slice(0, 60)}</span>
                <span className="gc-room-meta">{t('xGcc.members', { n: r.members })} · {when(r.last_msg_at || r.created_at)}</span>
              </button>
            ))}
          </aside>

          {/* ---- the chat */}
          <section className="gc-main">
            {!room ? (
              <div className="gc-empty"><MessagesSquare size={28} /><span>{t('xGcc.pickRoom')}</span></div>
            ) : (
              <>
                <div className="gc-head">
                  {/* phones: back to the rooms (the list and the room take the screen in turn) */}
                  <button type="button" className="gc-back" onClick={() => setRoom(null)} aria-label={t('xGcc.rooms')} data-gc-back><ChevronLeft size={20} /></button>
                  <div className="gc-head-name">
                    <h3 data-gc-roomname>{room.name}</h3>
                    {/* the faces open the room's people — and, for the owner, Remove beside each (Jay 30/10) */}
                    <button type="button" className="gc-stack gc-stack-btn" title={members.map((m) => m.name).join(', ')} onClick={() => setDlg({ kind: 'members' })} data-gc-members>
                      {members.slice(0, 6).map((m) => <UserAvatar key={m.id} url={m.avatar} name={m.name} size={24} />)}
                      <span className="gc-muted">{t('xGcc.members', { n: members.length })}</span>
                    </button>
                  </div>
                  <div className="gc-head-btns">
                    {/* the room in a small window on every page (Amy FB-0002): pop out, and back to where the person was */}
                    <button type="button" className="pa-btn gc-popout" title={t('xGcc.popOutHint')} onClick={() => { popOutRoom(roomPk); if (window.history.length > 1) navigate(-1); else navigate('/'); }} data-gc-popout>
                      <PictureInPicture2 size={14} /> {t('xGcc.popOut')}
                    </button>
                    {catchyOn && (
                      <button type="button" className="pa-btn gc-sidesheet-btn" onClick={() => setSideSheet(true)} data-gc-sidesheet>
                        <Mascot className="gc-bird-xs" /> {t('xGcc.sideTitle')}{side.length > 0 ? ` (${side.length})` : ''}
                      </button>
                    )}
                    <label className={`gc-switch gc-catchy-sw ${catchyOn ? 'on' : 'off'}`} title={catchyOn ? t('xGcc.catchyOnHint') : t('xGcc.catchyOffHint')}>
                      <input type="checkbox" checked={catchyOn} onChange={toggleCatchy} data-gc-catchyswitch />
                      <span className="gc-track" aria-hidden="true"><span /></span>
                      <span className="gc-switch-l">
                        <Mascot className={`gc-bird-xs${catchyOn ? '' : ' gc-asleep'}`} /> Catchy · {catchyOn ? t('xGcc.catchyLive') : <>{t('xGcc.catchySleeping')} 💤</>}
                      </span>
                    </label>
                    <label className={`gc-switch gc-tr-sw ${trOn ? 'on' : 'off'}`} title={trOn ? t('xGcc.trOnHint') : t('xGcc.trOffHint')}>
                      <input type="checkbox" checked={trOn} onChange={toggleTr} data-gc-trswitch />
                      <span className="gc-track" aria-hidden="true"><span /></span>
                      <span className="gc-switch-l"><Languages size={14} /> {t('xGcc.trSwitch')}</span>
                    </label>
                    <button type="button" className="pa-btn" onClick={() => setDlg({ kind: 'add', picked: new Set() })} data-gc-add><UserPlus size={14} /> {t('xGcc.addPeople')}</button>
                    <button type="button" className="pa-btn" onClick={() => setDlg({ kind: 'rename', name: room.name })}><Pencil size={14} /> {t('xGcc.rename')}</button>
                    {room.role === 'owner'
                      ? <button type="button" className="pa-btn gc-danger" onClick={() => setDlg({ kind: 'delete' })}><Trash2 size={14} /> {t('xGcc.deleteRoom')}</button>
                      : <button type="button" className="pa-btn gc-danger" onClick={() => setDlg({ kind: 'leave' })}><LogOut size={14} /> {t('xGcc.leave')}</button>}
                  </div>
                </div>
                <div className="gc-thread" ref={threadRef} data-gc-thread>
                  {!main.length && <p className="gc-muted gc-center">{t('xGcc.noMessages')}</p>}
                  {earlierUntranslated && (
                    <div className="gc-trabove"><button type="button" className="pa-btn" onClick={translateAbove} data-gc-trabove><Languages size={14} /> {t('xGcc.trAbove')}</button></div>
                  )}
                  {main.map((m) => {
                    if (m.kind === 'system') return <div key={m.msg_pk} className="gc-sys">{m.body} · {when(m.created_at)}</div>;
                    if (m.kind === 'catchy') {
                      return (
                        <div key={m.msg_pk} className="gc-row" data-gc-catchy data-gc-msg={m.msg_pk}>
                          <Mascot className="gc-bird" />
                          <div className="gc-col">
                            <div className="gc-who gc-who-ai" title={t('xGcc.aiMark')}>Catchy</div>
                            <div className="gc-bubble gc-ai">{said(m)}<Sources list={m.sources} /></div>
                            {renderReacts(m)}
                            {renderTime(m)}
                            {renderSeen(m)}
                          </div>
                        </div>
                      );
                    }
                    const p = byId.get(m.user_id);
                    if (mine(m)) {
                      return (
                        <div key={m.msg_pk} className="gc-row me" data-gc-mine data-gc-msg={m.msg_pk}>
                          <div className="gc-col">
                            {editing && editing.msgPk === m.msg_pk ? renderEdit() : <div className="gc-bubble gc-me">{renderQuote(m)}{said(m)}</div>}
                            {renderReacts(m)}
                            {renderTime(m)}
                            {renderSeen(m)}
                          </div>
                          <UserAvatar url={me.avatarUrl} name={me.name} size={30} />
                        </div>
                      );
                    }
                    return (
                      <div key={m.msg_pk} className="gc-row" data-gc-msg={m.msg_pk}>
                        <UserAvatar url={p?.avatar} name={p?.name || m.user_name} size={30} />
                        <div className="gc-col">
                          <div className="gc-who">{p?.name || m.user_name}</div>
                          <div className={`gc-bubble${(m.mentions || []).some((x) => Number(x.id) === Number(me.userId)) ? ' gc-for-me' : ''}`}>{renderQuote(m)}{said(m)}</div>
                          {renderReacts(m)}
                          {renderTime(m)}
                          {renderSeen(m)}
                        </div>
                      </div>
                    );
                  })}
                  {catchy.answering && <div className="gc-row"><Mascot className="gc-bird" /><div className="gc-typing" data-gc-answering><i /><i /><i /> {t('xGcc.answering')}</div></div>}
                </div>
                {pending.length > 0 && (
                  <div className="gc-pending" data-gc-pending>
                    {pending.map((f) => (
                      <span key={f.key} className="gc-pend" title={f.file.name}>
                        {f.url ? <img src={f.url} alt={f.file.name} /> : <FileText size={18} />}
                        <span className="gc-pend-n">{f.file.name}<em>{fileSize(f.file.size)}</em></span>
                        <button type="button" aria-label={t('xGcc.removeFile')} title={t('xGcc.removeFile')}
                          onClick={() => { if (f.url) URL.revokeObjectURL(f.url); setPending((p) => p.filter((x) => x.key !== f.key)); }}><X size={13} /></button>
                      </span>
                    ))}
                  </div>
                )}
                {sched.length > 0 && (
                  <div className={`gc-sched${schedOpen ? ' open' : ''}`} data-gc-sched>
                    <button type="button" className="gc-sched-head" onClick={() => setSchedOpen((v) => !v)} aria-expanded={schedOpen} data-gc-schedhead>
                      <CalendarClock size={14} /> {t('xGcc.schedN', { n: sched.length })}
                    </button>
                    {schedOpen && sched.map((x) => (
                      <div key={x.id} className="gc-sched-row" data-gc-schedrow={x.id}>
                        <span className="gc-sched-at">{fmtAt(x.sendAt)}</span>
                        <span className="gc-sched-body">{x.body || '—'}{x.files ? ` · 📎 ${x.files}` : ''}</span>
                        <button type="button" className="pa-btn small primary" onClick={() => schedAct(x.id, 'send')} data-gc-schedsend={x.id}><Send size={12} /> {t('xGcc.schedSendNow')}</button>
                        <button type="button" className="pa-btn small gc-danger" onClick={() => schedAct(x.id, 'cancel')} data-gc-schedcancel={x.id}><X size={12} /> {t('xGcc.schedCancel')}</button>
                      </div>
                    ))}
                  </div>
                )}
                {replyTo && (
                  <div className="gc-replybar" data-gc-replybar>
                    <Reply size={14} />
                    <span>{t('xGcc.replyingTo', { name: replyTo.kind === 'catchy' ? 'Catchy' : (byId.get(replyTo.user_id)?.name || replyTo.user_name || '—') })}</span>
                    <em>{String(replyTo.body || '').replace(/\s+/g, ' ').slice(0, 90)}</em>
                    <button type="button" aria-label={t('xGcc.cancel')} title={t('xGcc.cancel')} onClick={() => setReplyTo(null)} data-gc-replycancel><X size={13} /></button>
                  </div>
                )}
                {fmtOn && (
                  <div className="gc-fmtbar" role="toolbar" aria-label={t('xGcc.format')} data-gc-fmtbar>
                    {FMT.map(({ k, Icon, act, label, keys, sep }) => (
                      <React.Fragment key={k}>
                        {sep && <span className="gc-fmt-sep" aria-hidden="true" />}
                        <button type="button" className="gc-fmt" title={keys ? `${t(`xGcc.${label}`)} (${keys})` : t(`xGcc.${label}`)} aria-label={t(`xGcc.${label}`)}
                          onMouseDown={(e) => e.preventDefault()} onClick={act} data-gc-fmt={k}><Icon size={16} /></button>
                      </React.Fragment>
                    ))}
                  </div>
                )}
                <div className="gc-compose" onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files')) e.preventDefault(); }}
                  onDrop={(e) => { if (e.dataTransfer?.files?.length) { e.preventDefault(); addFiles(e.dataTransfer.files); } }}>
                  <button type="button" className="pa-btn gc-at-btn" disabled={!catchyOn} title={catchyOn ? t('xGcc.mention') : t('xGcc.turnOnToAsk')}
                    onClick={() => { setText((x) => (/@catchy/i.test(x) ? x : `@Catchy ${x}`)); inputRef.current?.focus(); }} data-gc-at>@Catchy</button>
                  <div className="gc-input-wrap">
                    <textarea ref={inputRef} rows={2} value={text} placeholder={isSmall() ? (catchyOn ? t('xGcc.placeholderTouch') : t('xGcc.placeholderTouchOff')) : (catchyOn ? t('xGcc.placeholder') : t('xGcc.placeholderOff'))} data-gc-input
                      onChange={(e) => { setText(e.target.value); watchTag(e.target.value, e.target.selectionStart); }}
                      onClick={(e) => watchTag(e.target.value, e.target.selectionStart)} onPaste={onPaste}
                      onKeyDown={(e) => {
                        if (tagQ && tagChoices.length) {
                          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setTagQ((x) => ({ ...x, i: (x.i + (e.key === 'ArrowDown' ? 1 : tagChoices.length - 1)) % tagChoices.length })); return; }
                          if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickTag(tagChoices[tagQ.i] || tagChoices[0]); return; }
                          if (e.key === 'Escape') { e.preventDefault(); setTagQ(null); return; }
                        }
                        if ((e.ctrlKey || e.metaKey) && !e.altKey) {
                          const k = e.key.toLowerCase();
                          if (!e.shiftKey && k === 'b') { e.preventDefault(); wrapSel('**'); return; }
                          if (!e.shiftKey && k === 'i') { e.preventDefault(); wrapSel('_'); return; }
                          if (!e.shiftKey && k === 'u') { e.preventDefault(); wrapSel('__'); return; }
                          if (e.shiftKey && k === 'x') { e.preventDefault(); wrapSel('~~'); return; }
                        }
                        if (e.key === 'Enter' && e.shiftKey && !e.nativeEvent.isComposing && listEnter(e)) return;
                        if (enterSends(e)) { e.preventDefault(); sendMsg(); }   // a phone's Enter is a new line (touchInput.js)
                      }} />
                    {tagQ && tagChoices.length > 0 && (
                      <div className="gc-tagpop" role="listbox" data-gc-tagpop>
                        {tagChoices.map((p, i) => (
                          <button key={p.id} type="button" className={i === tagQ.i ? 'on' : ''} onMouseDown={(e) => { e.preventDefault(); pickTag(p); }} data-gc-tagopt={p.id}>
                            {p.id === 'catchy' ? <Mascot className="gc-bird-xs" /> : p.id === 'all' ? <span className="gc-tag-all" aria-hidden="true">@</span> : <UserAvatar url={p.avatar} name={p.name} size={22} />}<span>{p.label || p.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="gc-tools">
                    <button type="button" className={`gc-tool${fmtOn ? ' on' : ''}`} title={t('xGcc.format')} aria-label={t('xGcc.format')} aria-pressed={fmtOn}
                      onClick={() => { setFmtOn((v) => !v); inputRef.current?.focus(); }} data-gc-fmtbtn><Baseline size={18} /></button>
                    <button type="button" className="gc-tool" title={t('xGcc.emoji')} aria-label={t('xGcc.emoji')} onClick={(e) => setEmojiAt(emojiAt ? null : e.currentTarget)} data-gc-emojibtn><Smile size={18} /></button>
                    <label className="gc-tool" title={t('xGcc.attach')} aria-label={t('xGcc.attach')} data-gc-attach>
                      <Paperclip size={18} />
                      <input type="file" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-gc-fileinput />
                    </label>
                    {/* phones: the camera straight away (Jay task #1172) */}
                    <label className="gc-tool gc-camera" title={t('xGcc.camera')} aria-label={t('xGcc.camera')} data-gc-camera>
                      <Camera size={18} />
                      <input type="file" accept="image/*" capture="environment" hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-gc-camerainput />
                    </label>
                  </div>
                  <div className="gc-sendgroup">
                    <button type="button" className="pa-btn primary gc-send" disabled={busy || (!text.trim() && !pending.length)} onClick={sendMsg} data-gc-send><Send size={15} /> {busy && pending.length ? t('xGcc.uploading') : t('xGcc.send')}</button>
                    <button type="button" className="pa-btn gc-schedbtn" disabled={busy || (!text.trim() && !pending.length)} title={t('xGcc.schedule')} aria-label={t('xGcc.schedule')}
                      onClick={(e) => { setCustOpen(false); setSchedAt(schedAt ? null : e.currentTarget); }} data-gc-schedbtn><ChevronUp size={16} /></button>
                  </div>
                </div>
                {/* THE NOTICE (Jay, tasks #1169 / #1170): the DAC VN Dashboard's line under Catchy, and that Catchy reads the room for the
                    Portal's issues and ideas — in the reader's portal language */}
                <div className="gc-notice" data-catchy-notice>{t('ask.note')} {t('xGcc.readsNote')}</div>
                {schedAt && ReactDOM.createPortal(
                  <div className="gc-schedpop-back" onMouseDown={(e) => { if (e.target === e.currentTarget) setSchedAt(null); }}>
                    <div className="gc-schedpop" role="dialog" aria-label={t('xGcc.schedule')} data-gc-schedpop
                      style={{ right: Math.max(8, window.innerWidth - schedAt.getBoundingClientRect().right), bottom: Math.max(8, window.innerHeight - schedAt.getBoundingClientRect().top + 6) }}>
                      <div className="gc-schedpop-h">{t('xGcc.schedule')}</div>
                      {presets().map((p) => (
                        <button key={p.k} type="button" className="gc-schedopt" disabled={busy} onClick={() => scheduleMsg(p.at)} data-gc-schedopt={p.k}>{p.label}</button>
                      ))}
                      <div className="gc-schedpop-sep" />
                      <button type="button" className="gc-schedopt gc-schedopt-custom" aria-expanded={custOpen} onClick={() => setCustOpen((v) => !v)} data-gc-schedpick>
                        <CalendarDays size={18} /> {t('xGcc.schedCustom')}
                      </button>
                      {custOpen && (
                        <div className="gc-schedcustom">
                          <input type="datetime-local" value={custom} onChange={(e) => setCustom(e.target.value)} aria-label={t('xGcc.schedCustom')} data-gc-schedcustom />
                          <button type="button" className="pa-btn primary" disabled={!custom || busy} onClick={() => scheduleMsg(custom)} data-gc-schedgo><CalendarClock size={14} /> {t('xGcc.schedGo')}</button>
                        </div>
                      )}
                    </div>
                  </div>, document.body)}
                {emojiAt && <EmojiPop anchor={emojiAt} list={EMOJIS} onPick={(e) => { insertAtCaret(e); }} onClose={() => setEmojiAt(null)} testid="compose" />}
                {reactAt && <EmojiPop anchor={document.querySelector(`[data-gc-reactadd="${reactAt.msgPk}"]`)} list={QUICK} onPick={(e) => react(reactAt.msgPk, e)} onClose={() => setReactAt(null)} testid="react" />}
                {asksCatchyAsleep && <p className="gc-asleep-hint" data-gc-asleephint>💤 {t('xGcc.asleepHint')}</p>}
              </>
            )}
          </section>

          {/* ---- Catchy's side window */}
          {room && !sideOpen && (
            <button type="button" className="gc-side-rail" onClick={toggleSide} title={t('xGcc.sideExpand')} aria-label={t('xGcc.sideExpand')} data-gc-siderail>
              <ChevronsLeft size={16} />
              <Mascot className={`gc-bird-xs${catchyOn ? '' : ' gc-asleep'}`} />
              <span className="gc-rail-label">{catchyOn ? t('xGcc.sideTitle') : `Catchy 💤`}</span>
              {catchyOn && side.length > 0 && <span className="gc-rail-n">{side.length}</span>}
            </button>
          )}
          {room && (sideOpen || sideSheet) && (
            <aside className={`gc-side${sideSheet ? ' sheet' : ''}`} data-gc-sidewin>
              <div className="gc-side-head">
                {/* FOLDED FROM ITS OWN EDGE (Max, 28/10: "thay biểu tượng này bằng cái expand hay collapse trực tiếp bên Catchy live
                    section") — the head switch is gone; the window folds into a rail and opens from it, per person, kept per room */}
                <button type="button" className="gc-side-fold" onClick={() => (sideSheet ? setSideSheet(false) : toggleSide())} title={t('xGcc.sideCollapse')} aria-label={t('xGcc.sideCollapse')} data-gc-sidefold>
                  <ChevronsRight size={16} />
                </button>
                <Mascot className={`gc-bird${catchyOn ? '' : ' gc-asleep'}`} />
                <div><b>{catchyOn ? t('xGcc.sideTitle') : 'Catchy'}</b>{catchyOn ? <span className="gc-live">LIVE</span> : <span className="gc-live gc-sleep-tag">SLEEPING</span>}{catchyOn && <p>{t('xGcc.sideSub')}</p>}</div>
              </div>
              {!catchyOn && (
                <div className="gc-sleeping" data-gc-sleeping>
                  <span className="gc-zz" aria-hidden="true">💤</span>
                  <Mascot className="gc-bird gc-asleep" />
                  <b>{t('xGcc.offTitle')}</b>
                  <p>{t('xGcc.offLine')}</p>
                  <button type="button" className="pa-btn" onClick={toggleCatchy} data-gc-wake>{t('xGcc.turnOn')}</button>
                </div>
              )}
              <div className="gc-side-list" ref={sideRef}>
                {!side.length && catchyOn && <p className="gc-muted">{t('xGcc.sideEmpty')}</p>}
                {side.map((m) => (
                  <div key={m.msg_pk} className="gc-sidepost" data-gc-sidepost title={t('xGcc.aiMark')}>
                    <div className="gc-sidepost-time">{when(m.created_at)}</div>
                    {said(m)}
                    <Sources list={m.sources} />
                  </div>
                ))}
                {catchy.watching && <div className="gc-typing gc-side-typing"><i /><i /><i /> {t('xGcc.sideWatching')}</div>}
              </div>
              {/* the last time it read the room and had nothing to add — so silence reads as "nothing new", not as broken
                  (Max, 27/10: "phần Catchy live ko thấy work á") */}
              {!catchy.watching && catchy.lastRun && catchy.lastRun.outcome !== 'posted' && (
                <div className="gc-side-foot" data-gc-sidelast>{t('xGcc.sideLast', { t: when(catchy.lastRun.at) })}</div>
              )}
            </aside>
          )}
        </div>
      </div>

      {dlg && (
        <Dialog
          title={{ new: t('xGcc.newRoom'), add: t('xGcc.addPeople'), rename: t('xGcc.rename'), leave: t('xGcc.leave'), delete: t('xGcc.deleteRoom'), members: t('xGcc.membersTitle'), delmsg: t('xGcc.deleteMsg') }[dlg.kind]}
          onClose={() => setDlg(null)}
          footer={(
            <>
              {dlg.err && <span className="gc-dlg-err">{dlg.err}</span>}
              {dlg.kind === 'members' && !dlg.removing ? (
                <button type="button" className="pa-btn" onClick={() => setDlg(null)}>{t('xGcc.close')}</button>
              ) : (<>
              <button type="button" className="pa-btn" onClick={() => (dlg.kind === 'members' ? setDlg((d) => ({ ...d, removing: null, err: '' })) : setDlg(null))}>{t('xGcc.cancel')}</button>
              <button type="button" className={`pa-btn primary ${dlg.kind === 'delete' || dlg.kind === 'leave' || dlg.kind === 'members' || dlg.kind === 'delmsg' ? 'gc-danger-fill' : ''}`} data-gc-ok
                disabled={dlg.busy || ((dlg.kind === 'new' || dlg.kind === 'rename') && !String(dlg.name || '').trim()) || (dlg.kind === 'add' && !dlg.picked.size)}
                onClick={doDlg}>
                {{ new: t('xGcc.create'), add: t('xGcc.add'), rename: t('xGcc.save'), leave: t('xGcc.leave'), delete: t('xGcc.deleteRoom'), members: t('xGcc.remove'), delmsg: t('xGcc.deleteMsg') }[dlg.kind]}
              </button>
              </>)}
            </>
          )}
        >
          {(dlg.kind === 'new' || dlg.kind === 'rename') && (
            <label className="gc-field">{t('xGcc.roomName')}
              <input value={dlg.name} maxLength={120} placeholder={t('xGcc.roomNamePh')} onChange={(e) => setDlg((d) => ({ ...d, name: e.target.value }))} data-gc-name autoFocus />
            </label>
          )}
          {(dlg.kind === 'new' || dlg.kind === 'add') && (
            <div className="gc-field">{t('xGcc.people')}
              <PeoplePicker people={people} groups={groups} exclude={dlg.kind === 'add' ? memberIds : new Set([Number(me.userId)])} picked={dlg.picked} setPicked={setPicked} />
            </div>
          )}
          {dlg.kind === 'members' && !dlg.removing && (
            <div className="gc-members" data-gc-memberlist>
              {members.map((m) => (
                <div key={m.id} className="gc-member" data-gc-member={m.id}>
                  <UserAvatar url={m.avatar} name={m.name} size={28} />
                  <span className="gc-member-name">{m.name}{Number(m.id) === Number(me.userId) ? ` ${t('xGcc.youTag')}` : ''}</span>
                  {m.role === 'owner' && <span className="gc-owner-tag">{t('xGcc.owner')}</span>}
                  {room?.role === 'owner' && Number(m.id) !== Number(me.userId) && (
                    <button type="button" className="pa-btn gc-danger gc-remove" onClick={() => setDlg((d) => ({ ...d, removing: m, err: '' }))} data-gc-remove={m.id}>
                      <UserMinus size={14} /> {t('xGcc.remove')}
                    </button>
                  )}
                </div>
              ))}
              {room?.role !== 'owner' && <p className="gc-muted">{t('xGcc.onlyOwnerRemoves')}</p>}
            </div>
          )}
          {dlg.kind === 'members' && dlg.removing && <p>{t('xGcc.confirmRemove', { name: dlg.removing.name, room: room?.name })}</p>}
          {dlg.kind === 'leave' && <p>{t('xGcc.confirmLeave', { name: room?.name })}</p>}
          {dlg.kind === 'delete' && <p>{t('xGcc.confirmDelete', { name: room?.name })}</p>}
          {dlg.kind === 'delmsg' && <p data-gc-delmsgtext>{t('xGcc.confirmDeleteMsg')}</p>}
        </Dialog>
      )}
    </div>
  );
}
